"use client";
import { useTranslations } from "next-intl";

import { useState } from "react";
import Image from "next/image";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import CustomButton from "../ui/custom-button";
import { useIsMobileLandscape } from "@/hooks/use-mobile-landscape";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRouter } from "next/navigation";
import { SECTION_CONTAINER, SECTION_HEADING, SECTION_PADDING } from "@/lib/home-section-layout";

interface ServiceCategory {
  id: string;
  title: string;
  isOpen?: boolean;
  services: Service[];
}

interface Service {
  id: string;
  name: string;
  image: string;
  title: string;
  description: string;
  facts: string;
}

interface ServicesSectionProps {
  className?: string;
  sectionTitle?: string;
  sectionDescription?: string;
  viewAllServicesLabel?: string;
  viewNextServiceLabel?: string;
  categories?: ServiceCategory[];
}

export function ServicesSection({
  className,
  categories = [],
}: ServicesSectionProps) {
  const t = useTranslations('services');
  const isMobileLandscape = useIsMobileLandscape();
  const isMobile = useIsMobile();
  const router = useRouter();
  const [activeCategory, setActiveCategory] = useState(categories[0].id);
  const [activeService, setActiveService] = useState(
    categories[0].services[0].id
  );
  const [categoryStates, setCategoryStates] = useState(
    categories.reduce((acc, category) => {
      acc[category.id] = category.isOpen || false;
      return acc;
    }, {} as Record<string, boolean>)
  );

  const toggleCategory = (categoryId: string) => {
    const wasOpen = categoryStates[categoryId];

    if (wasOpen) {
      // Close the category
      setCategoryStates((prev) => ({
        ...prev,
        [categoryId]: false,
      }));
    } else {
      // Open the category and set it as active with its first service
      openSingleCategory(categoryId);
      setActiveCategory(categoryId);

      // Find the category and set the first service as active
      const category = categories.find(cat => cat.id === categoryId);
      if (category && category.services.length > 0) {
        setActiveService(category.services[0].id);
      }
    }
  };

  const handleServiceClick = (categoryId: string, serviceId: string) => {
    setActiveCategory(categoryId);
    setActiveService(serviceId);
  };

  const openSingleCategory = (categoryId: string) => {
    setCategoryStates((prev) => {
      const newState = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);
      newState[categoryId] = true;
      return newState;
    });
  };

  const handleNextService = () => {
    const currentCategoryIndex = categories.findIndex(
      (category) => category.id === activeCategory
    );
    const currentCategory = categories[currentCategoryIndex];
    const currentServiceIndex = currentCategory.services.findIndex(
      (service) => service.id === activeService
    );

    // If there's another service in the current category
    if (currentServiceIndex < currentCategory.services.length - 1) {
      setActiveService(currentCategory.services[currentServiceIndex + 1].id);
      return;
    }

    // If there's another category
    if (currentCategoryIndex < categories.length - 1) {
      const nextCategory = categories[currentCategoryIndex + 1];
      setActiveCategory(nextCategory.id);
      setActiveService(nextCategory.services[0].id);
      openSingleCategory(nextCategory.id);
      return;
    }

    // If we're at the last service of the last category, loop back to the first
    setActiveCategory(categories[0].id);
    setActiveService(categories[0].services[0].id);
    openSingleCategory(categories[0].id);
  };

  // Find the current active service
  const currentCategory = categories.find(
    (category) => category.id === activeCategory
  );
  const currentService = currentCategory?.services.find(
    (service) => service.id === activeService
  );

  return (
    <section id="services" data-surface="base" className={cn(SECTION_PADDING, "font-outfit bg-site", className)}>
      <div className={cn(SECTION_CONTAINER, isMobileLandscape && "!mx-6")}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left side - Categories and services */}
          <div>
            <h2 className={SECTION_HEADING}>
              {t('sectionTitle')}
            </h2>
            <p className="text-white mb-8">
              {t('sectionDescription')}
            </p>

            {categories.map((category) => (
              <div key={category.id} className="   ">
                <button
                  className="flex items-center justify-between w-full text-left"
                  onClick={() => toggleCategory(category.id)}
                >
                  <h3 className="text-xl py-2 font-semibold text-white">
                    {category.title}
                  </h3>
                  {categoryStates[category.id] ? (
                    <ChevronUp className="h-5 w-5 text-yellow-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-yellow-500" />
                  )}
                </button>

                {categoryStates[category.id] && (
                  <div className="flex flex-wrap gap-2  mb-4">
                    {category.services.map((service) => (
                      <button
                        key={service.id}
                        className={cn(
                          "px-4 py-2 rounded-full text-sm transition-colors",
                          activeService === service.id
                            ? "bg-yellow-500 text-black"
                            : "bg-zinc-800 text-white hover:bg-zinc-700"
                        )}
                        onClick={() =>
                          handleServiceClick(category.id, service.id)
                        }
                      >
                        {service.name}
                      </button>
                    ))}
                  </div>
                )}

                <div className="border-b border-yellow-500 my-4"></div>
              </div>
            ))}
            <div className="flex justify-center mt-4">
              <CustomButton
                variant="capsule"
                onClick={() => router.push("/services/auto-services")}
                size={isMobile ? "md" : "lg"}
              >
                {t('viewAllServices')}
              </CustomButton>
            </div>
          </div>

          {/* Right side - Service details */}
          {currentService && (
            <div
              className={cn(
                "flex flex-col xl:flex-row gap-4 md:p-4",
                isMobileLandscape && "!flex-col "
              )}
            >
              <div
                className={cn(
                  "relative w-full aspect-[2/2] xl:aspect-[4/3] rounded-3xl overflow-hidden",
                  isMobileLandscape && "!h-[250px] !w-full"
                )}
              >
                <div className="absolute top-0 left-0 bg-gradient-to-b from-black to-transparent p-6 z-10 w-full">
                  <h3 className="text-xs md:text-xl font-semibold text-yellow-500">
                    {currentService.title}
                  </h3>
                </div>
                <Image
                  src={
                    currentService.image ||
                    "/placeholder.svg?height=300&width=500&query=car mechanic working"
                  }
                  alt={currentService.title}
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="object-cover object-center"
                />
              </div>

              <div className="flex flex-col items-center justify-between gap-4">
                <div
                  className={cn(
                    "bg-yellow-500 rounded-3xl p-6  xl:w-[250px] h-full flex flex-col items-center justify-center",
                    isMobileLandscape && "!w-full "
                  )}
                >
                  <p className="text-black text-center align-center">
                    {currentService.facts}
                  </p>
                </div>

                <div
                  className={cn(
                    " hidden xl:flex  justify-center w-[250px] h-[100px] bg-site-card rounded-3xl",
                    isMobileLandscape && "!hidden"
                  )}
                >
                  <button
                    onClick={handleNextService}
                    className="flex items-center justify-center text-[24px] text-yellow-500 hover:text-yellow-400 transition-colors"
                  >
                    {t('viewNextService')} <ChevronRight className="h-8 w-8 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
